// packages/content/src/strings/index.ts
import { stringModules } from '../strings.gen';

type Banks = typeof stringModules;
export type StringKey = { [N in keyof Banks & string]: `${N}.${keyof Banks[N] & string}` }[keyof Banks & string];
export type StringParams = Readonly<Record<string, string | number>>;
export const strings: Banks = stringModules;

const NUM = new Intl.NumberFormat('en-US');
const TOKEN = /\{(\w+)(?::(num|a|A|lc|cap)|\|([^|}]*)\|([^}]*))?\}/g;
const CURRENCY = /\{(\w+)\|currency\}/g;                                     // revision 9 item 9
/** Name params every t() call gets by default (explicit params win). The keys are the only places these names are written (§7.1). */
const NAME_KEYS = { app: 'common.appName', shopkeeper: 'shop.shopkeeperName', curator: 'curator.name' } as const;

/** A thing's name as written mid-sentence: item names are labels ('Fishing rod', 'Ammonite'), so the first letter drops to lower case,
 *  unless the name opens with an abbreviation or acronym ('T. rex skull'). */
export function midSentence(name: string): string {
  return name.length > 1 && /[A-Z.]/.test(name[1]!) ? name : name.charAt(0).toLowerCase() + name.slice(1);
}
/** 'a' or 'an' for a word, by its sound as far as spelling tells ('an ammonite', 'a unicorn', 'an hour'). */
export function article(word: string): 'a' | 'an' {
  const w = word.toLowerCase();
  if (/^(uni|use|usu|uk|eu|one|once)/.test(w)) return 'a';
  return /^([aeiou]|hour|honest|heir)/.test(w) ? 'an' : 'a';
}
/** '{name}' → param; '{n:num}' → 12,480; '{n|resident|residents}' → singular when n === 1; '{x:a}' / '{x:A}' → 'a/an x' / 'A/An x' with
 *  x mid-sentence; '{x:lc}' → x mid-sentence; '{x:cap}' → x with a capital first letter (a sentence start). Unknown params stay verbatim.
 *  ('{n|currency}' is expanded by t() before this runs, revision 9 item 9.) */
export function format(template: string, params?: StringParams): string {
  return template.replace(TOKEN, (whole, name: string, mod?: string, one?: string, many?: string) => {
    const v = params?.[name];
    if (v === undefined) return whole;
    if (mod === 'num') return typeof v === 'number' ? NUM.format(v) : String(v);
    if (mod === 'lc') return midSentence(String(v));
    if (mod === 'cap') { const x = String(v); return x.charAt(0).toUpperCase() + x.slice(1); }
    if (mod === 'a' || mod === 'A') {
      const x = midSentence(String(v)), a = article(x);
      return `${mod === 'A' ? a.charAt(0).toUpperCase() + a.slice(1) : a} ${x}`;
    }
    if (one !== undefined) return Number(v) === 1 ? one : (many ?? '');
    return String(v);
  });
}

function lookup(key: string): string | undefined {
  const dot = key.indexOf('.');
  if (dot < 1) return undefined;
  const bank = (strings as Readonly<Record<string, Readonly<Record<string, string>>>>)[key.slice(0, dot)];
  return bank?.[key.slice(dot + 1)];
}
export function nameParams(): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [param, key] of Object.entries(NAME_KEYS)) { const v = lookup(key); if (v !== undefined) out[param] = v; }
  return out;
}
/** '{n|currency}' → '{n|<common.currency>|<common.currencyPlural>}', so no string ever writes the currency word (revision 9 item 9). */
function expandCurrency(template: string): string {
  return template.replace(CURRENCY, (_w, n: string) => `{${n}|${lookup('common.currency') ?? ''}|${lookup('common.currencyPlural') ?? ''}}`);
}
export function t(key: StringKey, params?: StringParams): string {
  const s = lookup(key);
  return s === undefined ? key : format(expandCurrency(s), { ...nameParams(), ...params });
}
export function hasString(key: string): key is StringKey {
  return lookup(key) !== undefined;
}
